import React, { FC, useCallback } from 'react';
import { Portal } from 'src/common/components/Portal';
import { Modal } from 'src/common/components/Modal';
import styled, { css } from 'styled-components';
import { media } from 'src/config/breakpoints';
import ChevronIcon from 'src/assets/icons/ChevronIcon';
import { colors, shadows } from 'src/config/colors';
import { Button, CleanButton } from 'src/common/components/Button';
import { useDispatch, useSelector } from 'react-redux';
import { courseListingActions } from 'src/state/courseListing/courseListingActions';
import { courseListingTotalResultsSelector } from 'src/state/courseListing/courseListingSelectors';
import { layers } from 'src/config/layers';
import { CourseListingCategoryFilter } from 'src/public/components/course-listing/CourseListingCategoryFilter';
import { CourseListingCourseLevelFilter } from 'src/public/components/course-listing/CourseListingCourseLevelFilter';
import { CourseListingSpeakerFilter } from 'src/public/components/course-listing/CourseListingSpeakerFilter';
import { eventListingCountLabelFormatter } from 'src/common/services/labelFormatter';

const BackButton = styled(CleanButton)`
  margin-left: 0.3125rem;

  svg {
    display: block;
  }
`;

const ClearButton = styled(CleanButton)`
  color: ${colors.pfp3bis};
  font-size: 0.875rem;
  line-height: 1.1875rem;
  margin-left: auto;
  text-transform: uppercase;
`;

const Content = styled.div`
  padding: 1.0625rem;
  width: 100%;

  ${media.w.min.px768(css`
    display: flex;
    flex-direction: column;
  `)};
`;

const ContentFooter = styled.div`
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  justify-content: center;
  padding: 3.125rem 0;

  ${media.w.max.px768(css`
    display: none;
  `)};
`;

const Filters = styled.div`
  ${media.w.min.px768(css`
    display: grid;
    grid-template-columns: auto auto;
    grid-template-rows: min-content min-content;
    justify-content: space-between;
    margin: 0 auto;
    width: 33.125rem;
  `)};
`;

const Footer = styled.div`
  box-shadow: ${shadows.modalFooter};
  flex: 0 0 auto;
  padding: 1rem;

  ${media.w.min.px768(css`
    display: none;
  `)};
`;

const FooterButton = styled(Button)`
  height: 2.5rem;
  width: 100%;
`;

const Header = styled.div`
  align-items: center;
  box-shadow: ${shadows.drop1};
  display: flex;
  flex: 0 0 auto;
  height: 3.3125rem;

  ${media.w.min.px768(css`
    height: 4rem;
  `)};
`;

const HeaderSide = styled.div`
  display: flex;
  flex: 0 0 4.375rem;
  padding: 0 0.75rem;

  ${media.w.min.px768(css`
    padding: 0 1.125rem;
  `)};
`;

const StyledCourseListingCategoryFilter = styled(CourseListingCategoryFilter)`
  ${media.w.min.px768(css`
    flex: 1 1 50%;
    order: 1;
  `)};
`;

const StyledCourseListingCourseLevelFilter = styled(CourseListingCourseLevelFilter)`
  ${media.w.min.px768(css`
    flex: 1 1 50%;
    order: 3;
  `)};
`;

const StyledCourseListingSpeakerFilter = styled(CourseListingSpeakerFilter)`
  ${media.w.min.px768(css`
    flex: 1 1 50%;
    order: 2;
  `)};
`;

const StyledModal = styled(Modal)`
  z-index: ${layers.overMobileMenu};

  ${media.w.min.px992(css`
    display: none;
  `)};
`;

const Title = styled.div`
  color: ${colors.blue};
  flex: 1 1 auto;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.1875rem;
  text-align: center;
  text-transform: uppercase;
`;

export interface CourseListingLayoutFiltersModalProps {
  onClose: () => void;
}

export const CourseListingFiltersModal: FC<CourseListingLayoutFiltersModalProps> = ({
  onClose,
}) => {
  const dispatch = useDispatch();
  const total = useSelector(courseListingTotalResultsSelector);

  const handleClearFilters = useCallback(
    () => dispatch(courseListingActions.clearFilters()),
    [dispatch],
  );

  return (
    <Portal>
      <StyledModal
        footer={
          <Footer>
            <FooterButton onClick={onClose}>
              Pokaż {eventListingCountLabelFormatter(total)}
            </FooterButton>
          </Footer>
        }
        header={
          <Header>
            <HeaderSide>
              <BackButton onClick={onClose}>
                <ChevronIcon direction="left" size={18} />
              </BackButton>
            </HeaderSide>

            <Title>FILTRY</Title>

            <HeaderSide>
              <ClearButton onClick={handleClearFilters}>WYCZYŚĆ</ClearButton>
            </HeaderSide>
          </Header>
        }
        onClose={onClose}
      >
        <Content>
          <Filters>
            <div>
              <StyledCourseListingCategoryFilter />
            </div>
            <div>
              <StyledCourseListingCourseLevelFilter />
              <StyledCourseListingSpeakerFilter />
            </div>
          </Filters>

          <ContentFooter>
            <Button onClick={onClose}>Pokaż {eventListingCountLabelFormatter(total)}</Button>
          </ContentFooter>
        </Content>
      </StyledModal>
    </Portal>
  );
};
